import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * ErrorBoundary — the CRM's last line of defense against a render crash.
 *
 * 2026-09-11: a shape mismatch between an API response and a page's expectations (`data.total` vs
 * the API's `totalCount`) threw during render and, with no error boundary anywhere in the tree,
 * unmounted the ENTIRE app — a blank white page right after a successful sign-in, which read to
 * Kirk as "I can't log in" (see PR #47/crm-web-fallback-masked-api-shape-mismatch). React only
 * unwinds to the nearest boundary above the failing component; with none, that's the document root.
 *
 * Wrapping the routed content here means a future render bug in any ONE page shows a recoverable
 * fallback instead of taking the whole app down, and logs the error to the console so it shows up
 * in a support screenshot or a browser dev console. No telemetry backend exists yet for crm-web —
 * this does not report anywhere — it only prevents total page loss and gives staff a way back in.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[CRM ErrorBoundary] a page crashed during render:', error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center bg-bg px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-line p-8 text-center space-y-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-fail-soft text-fail mx-auto">
            <AlertTriangle className="h-6 w-6" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-ink">Something went wrong on this page</h1>
          <p className="text-sm text-ink-muted">
            This one screen hit an error and couldn&apos;t finish loading. Your sign-in is still valid.
          </p>
          <div className="flex items-center justify-center gap-3 pt-1">
            <Button size="sm" variant="secondary" onClick={this.reset}>
              Try again
            </Button>
            <Button size="sm" onClick={() => window.location.assign('/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
          <details className="text-left pt-2">
            <summary className="text-xs text-ink-muted cursor-pointer select-none">Technical details</summary>
            <pre className="mt-2 text-xs text-ink-muted whitespace-pre-wrap break-words bg-bg rounded-lg p-3 border border-sunken">
              {error.message}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
